import { useState, useEffect, useRef } from "react";
import SEOHead from "../components/SEOHead";
import { motion } from "framer-motion";
import {
  User,
  Mail,
  Clock,
  Users,
  Loader2,
  CheckCircle,
  Crown,
  CalendarDays,
  ChevronDown,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { PhoneInputEmpire, getFullPhoneNumber } from "../components/PhoneInputEmpire";
import { sanitizeName } from "../lib/inputSanitizers";
import { recaptchaHeaders } from "../lib/recaptcha";
import { useScrollIntoViewWhenReady } from "../hooks/useScrollIntoViewWhenReady";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_SLOTS = [];
for (let h = 6; h <= 22; h++) {
  TIME_SLOTS.push(`${String(h).padStart(2, "0")}:00`);
  if (h < 22) TIME_SLOTS.push(`${String(h).padStart(2, "0")}:30`);
}

const formatTimeSlot = (t) => {
  const [hours, minutes] = t.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const h = hours % 12 || 12;
  return `${h}:${minutes.toString().padStart(2, "0")} ${period}`;
};

const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

export const PrivateSessionPage = () => {
  const [instructors, setInstructors] = useState([]);
  const [styles, setStyles] = useState([]);
  const [sessionTypes, setSessionTypes] = useState([]);
  const [, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [sessionNumber, setSessionNumber] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);

  // Validation states
  const [emailTouched, setEmailTouched] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const formSectionRef = useRef(null);
  useScrollIntoViewWhenReady(formSectionRef, sessionTypes.length > 0 && !submitted, { mobileOnly: true });

  const [formData, setFormData] = useState({
    guest_name: "",
    guest_email: "",
    guest_phone: "",
    instructor_id: "",
    preferred_date: "",
    preferred_time: "",
    duration_minutes: 60,
    session_type: "one_on_one",
    dance_style: "",
    goals: "",
    special_requests: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [instructorsRes, stylesRes, typesRes] = await Promise.all([
        axios.get(`${API}/instructors`),
        axios.get(`${API}/styles`),
        axios.get(`${API}/private-session-types`),
      ]);
      setInstructors(instructorsRes.data || []);
      setStyles(stylesRes.data || []);
      // Exclude couple plan
      setSessionTypes((typesRes.data || []).filter((t) => t.id !== "couple"));
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculatePrice = () => {
    const selectedType = sessionTypes.find((t) => t.id === formData.session_type);
    const pricePerHour = selectedType?.price_per_hour || 3000;
    return pricePerHour * (formData.duration_minutes / 60);
  };

  const isEmailValid = validateEmail(formData.guest_email);
  const [countryCode, setCountryCode] = useState("+254");
  const [isPhoneValid, setIsPhoneValid] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setEmailTouched(true);
    setPhoneTouched(true);

    if (
      !formData.guest_name ||
      !formData.guest_email ||
      !formData.guest_phone ||
      !formData.preferred_date ||
      !formData.preferred_time ||
      !formData.duration_minutes
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!isEmailValid) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (!isPhoneValid) {
      setPhoneTouched(true);
      toast.error(countryCode === "+254" 
        ? "Invalid Kenyan phone number format" 
        : "Please enter a valid phone number"
      );
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        ...formData,
        guest_phone: getFullPhoneNumber(countryCode, formData.guest_phone),
        instructor_id: formData.instructor_id === "any" ? "" : formData.instructor_id,
      };
      const rcHeaders = await recaptchaHeaders("private_session");
      const res = await axios.post(`${API}/private-sessions`, payload, { headers: rcHeaders });
      setSessionNumber(res.data.session_number);
      setSubmitted(true);
      toast.success("Private session request submitted!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSubmitted(false);
    setSelectedDate(null);
    setEmailTouched(false);
    setPhoneTouched(false);
    setCountryCode("+254");
    setIsPhoneValid(false);
    setFormData({
      guest_name: "",
      guest_email: "",
      guest_phone: "",
      instructor_id: "",
      preferred_date: "",
      preferred_time: "",
      duration_minutes: 60,
      session_type: "one_on_one",
      dance_style: "",
      goals: "",
      special_requests: "",
    });
  };

  if (submitted) {
    return (
      <div className="container-empire max-w-xl" data-testid="private-session-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <span className="font-cookie text-3xl text-empire-gold block mb-2">
            Request Received!
          </span>
          <h1 className="text-3xl font-bold text-white mb-4">
            We'll Contact You Soon
          </h1>
          <div className="glass rounded-xl p-4 mb-6">
            <p className="text-sm text-neutral-400 mb-1">Reference Number</p>
            <span className="text-xl font-mono font-bold text-empire-gold">
              {sessionNumber}
            </span>
          </div>
          <p className="text-neutral-400 text-sm mb-8">
            Our team will review your request and get back to you within 24 hours to confirm your private session.
          </p>
          <Button onClick={resetForm} className="btn-empire-outline">
            Book Another Session
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="container-empire max-w-4xl" data-testid="private-session-page">
      <SEOHead page="private" />
      {/* Hero */}
      <section className="text-center mb-8">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
          <span className="font-cookie text-4xl md:text-5xl text-empire-gold block mb-2">
            Personal attention
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Private Sessions
          </h1>
          <p className="text-neutral-400 text-lg max-w-xl mx-auto">
            Get personalized instruction tailored to your goals. Perfect for beginners, wedding couples, or anyone wanting focused training.
          </p>
        </motion.div>
      </section>

      {/* Session Types — mobile: horizontal tabs, desktop: cards */}

      {/* Mobile Tabs */}
      <div className="md:hidden flex gap-2 mb-8">
        {sessionTypes.map((item) => {
          const isSelected = formData.session_type === item.id;
          return (
            <button
              key={item.id}
              onClick={() => setFormData({ ...formData, session_type: item.id })}
              className={`flex-1 py-3 px-2 rounded-xl text-sm font-semibold transition-all ${isSelected
                  ? "bg-empire-gold text-black"
                  : "bg-neutral-900 text-neutral-400 border border-neutral-800 hover:border-neutral-600"
                }`}
              data-testid={`session-type-${item.id}`}
            >
              <div className="truncate">{item.name}</div>
              <div className={`text-xs mt-0.5 font-normal ${isSelected ? "text-black/70" : "text-neutral-500"}`}>
                KES {item.price_per_hour?.toLocaleString()}/hr
              </div>
            </button>
          );
        })}
      </div>

      {/* Desktop Cards */}
      <div className={`hidden md:grid gap-4 mb-8 ${sessionTypes.length === 2 ? "grid-cols-2" : "grid-cols-3"}`}>
        {sessionTypes.map((item) => {
          const isSelected = formData.session_type === item.id;
          const Icon = item.id === "one_on_one" ? User : Users;

          return (
            <motion.button
              key={item.id}
              onClick={() => setFormData({ ...formData, session_type: item.id })}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`p-6 rounded-2xl border-2 text-left transition-all ${isSelected
                  ? "border-empire-gold bg-empire-gold/10"
                  : "border-neutral-800 bg-neutral-900/50 hover:border-neutral-700"
                }`}
              data-testid={`session-type-${item.id}`}
            >
              <div className={`w-12 h-12 rounded-xl ${isSelected ? "bg-empire-gold/20" : "bg-neutral-800"} flex items-center justify-center mb-4`}>
                <Icon className={`w-6 h-6 ${isSelected ? "text-empire-gold" : "text-neutral-400"}`} />
              </div>
              <h3 className="text-lg font-bold text-white mb-1">{item.name}</h3>
              <p className="text-sm text-neutral-400 mb-2">{item.description}</p>
              <p className={`font-bold ${isSelected ? "text-empire-gold" : "text-neutral-300"}`}>
                KES {item.price_per_hour?.toLocaleString()}/hr
              </p>
            </motion.button>
          );
        })}
      </div>

      {/* Booking Form */}
      <motion.div
        ref={formSectionRef}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="scroll-mt-24 md:scroll-mt-0"
        data-testid="private-session-form-section"
      >
        <Card className="card-empire">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Crown className="w-5 h-5 text-empire-gold" />
              Book Your Private Session
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Contact Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Full Name */}
                <div className="space-y-2">
                  <Label className="text-white">Full Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500 pointer-events-none" />
                    <Input
                      type="text"
                      placeholder="John Doe"
                      value={formData.guest_name}
                      onChange={(e) => setFormData({ ...formData, guest_name: sanitizeName(e.target.value) })}
                      className="input-empire pl-10"
                      required
                    />
                  </div>
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label className="text-white">Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500 pointer-events-none z-10" />
                    <Input
                      type="email"
                      placeholder="john@example.com"
                      value={formData.guest_email}
                      onChange={(e) => {
                        setFormData({ ...formData, guest_email: e.target.value });
                        if (!emailTouched) setEmailTouched(true);
                      }}
                      onBlur={() => setEmailTouched(true)}
                      className={`input-empire pl-10 ${emailTouched && formData.guest_email && !isEmailValid
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                          : emailTouched && isEmailValid
                            ? "border-green-500/50"
                            : ""
                        }`}
                      required
                    />
                  </div>
                  {emailTouched && formData.guest_email && !isEmailValid && (
                    <p className="text-xs text-red-400">Enter a valid email address</p>
                  )}
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <Label className="text-white">Phone *</Label>
                  <PhoneInputEmpire
                    value={formData.guest_phone}
                    onChange={(val) => {
                      setFormData({ ...formData, guest_phone: val });
                      if (!phoneTouched && val) setPhoneTouched(true);
                    }}
                    countryCode={countryCode}
                    onCountryCodeChange={setCountryCode}
                    onValidChange={setIsPhoneValid}
                    touched={phoneTouched}
                    placeholder="712 345 678"
                  />
                  {formData.guest_phone && !isPhoneValid && (
                    <p className="text-xs text-red-400">
                      {countryCode === "+254" 
                        ? "Invalid Kenyan phone number format" 
                        : "Enter a valid phone number"}
                    </p>
                  )}
                </div>
              </div>

              {/* Preferred Date & Time */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Date Picker */}
                <div className="space-y-2">
                  <Label className="text-white">Preferred Date *</Label>
                  <Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className={`input-empire w-full flex items-center gap-2 text-left cursor-pointer ${!formData.preferred_date ? "text-neutral-500" : "text-white"
                          }`}
                      >
                        <CalendarDays className="w-5 h-5 text-neutral-500 shrink-0" />
                        <span className="flex-1 truncate">
                          {selectedDate ? format(selectedDate, "EEE, d MMM yyyy") : "Select a date"}
                        </span>
                        <ChevronDown className="w-4 h-4 text-neutral-500 shrink-0" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto p-0 bg-neutral-900 border-neutral-800 rounded-xl"
                      align="start"
                    >
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={(date) => {
                          setSelectedDate(date);
                          setFormData({
                            ...formData,
                            preferred_date: date ? format(date, "yyyy-MM-dd") : "",
                          });
                          setDatePopoverOpen(false);
                        }}
                        disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Time Picker */}
                <div className="space-y-2">
                  <Label className="text-white">Preferred Time *</Label>
                  <Select
                    value={formData.preferred_time}
                    onValueChange={(value) => setFormData({ ...formData, preferred_time: value })}
                  >
                    <SelectTrigger className="bg-neutral-900 border-neutral-800 h-12 rounded-xl text-white">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-neutral-500" />
                        <SelectValue placeholder="Select a time" />
                      </div>
                    </SelectTrigger>
                    <SelectContent className="bg-neutral-900 border-neutral-800 max-h-60">
                      {TIME_SLOTS.map((slot) => (
                        <SelectItem key={slot} value={slot} className="text-white hover:text-empire-gold">
                          {formatTimeSlot(slot)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Duration, Dance Style, Instructor */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Duration - required */}
                <div className="space-y-2">
                  <Label className="text-white">Duration *</Label>
                  <Select
                    value={String(formData.duration_minutes)}
                    onValueChange={(value) => setFormData({ ...formData, duration_minutes: Number(value) })}
                    required
                  >
                    <SelectTrigger className="bg-neutral-900 border-neutral-800 h-12 rounded-xl text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-neutral-900 border-neutral-800">
                      <SelectItem value="60">1 Hour</SelectItem>
                      <SelectItem value="90">1.5 Hours</SelectItem>
                      <SelectItem value="120">2 Hours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Dance Style */}
                <div className="space-y-2">
                  <Label className="text-white">Dance Style</Label>
                  <Select
                    value={formData.dance_style}
                    onValueChange={(value) => setFormData({ ...formData, dance_style: value })}
                  >
                    <SelectTrigger className="bg-neutral-900 border-neutral-800 h-12 rounded-xl text-white">
                      <SelectValue placeholder="Select style" />
                    </SelectTrigger>
                    <SelectContent className="bg-neutral-900 border-neutral-800">
                      {styles.map((style) => (
                        <SelectItem key={style.id} value={style.name}>
                          {style.name}
                        </SelectItem>
                      ))}
                      <SelectItem value="other">Other / Not Sure</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Preferred Instructor */}
                <div className="space-y-2">
                  <Label className="text-white">Preferred Instructor</Label>
                  <Select
                    value={formData.instructor_id}
                    onValueChange={(value) => setFormData({ ...formData, instructor_id: value })}
                  >
                    <SelectTrigger className="bg-neutral-900 border-neutral-800 h-12 rounded-xl text-white">
                      <SelectValue placeholder="Select instructor" />
                    </SelectTrigger>
                    <SelectContent className="bg-neutral-900 border-neutral-800">
                      <SelectItem value="any">Empire Dance Kenya</SelectItem>
                      {instructors.map((instructor) => (
                        <SelectItem key={instructor.id} value={instructor.id}>
                          {instructor.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Goals */}
              <div className="space-y-2">
                <Label className="text-white">Your Goals</Label>
                <Textarea
                  placeholder="What would you like to achieve? E.g., Wedding first dance, learn basics, prepare for performance..."
                  value={formData.goals}
                  onChange={(e) => setFormData({ ...formData, goals: e.target.value })}
                  className="input-empire min-h-[80px]"
                />
              </div>

              {/* Special Requests */}
              <div className="space-y-2">
                <Label className="text-white">Special Requests</Label>
                <Textarea
                  placeholder="Any specific songs, movements, or requirements?"
                  value={formData.special_requests}
                  onChange={(e) => setFormData({ ...formData, special_requests: e.target.value })}
                  className="input-empire min-h-[60px]"
                />
              </div>

              {/* Price Summary */}
              <div className="bg-neutral-900/50 rounded-xl p-4 border border-neutral-800">
                <div className="flex items-center justify-between">
                  <span className="text-neutral-400">Estimated Price</span>
                  <span className="text-2xl font-bold text-empire-gold">
                    KES {calculatePrice().toLocaleString()}
                  </span>
                </div>
                <p className="text-xs text-neutral-500 mt-2">
                  Final price confirmed after review. Payment collected at session.
                </p>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full btn-empire h-14 text-lg"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Crown className="w-5 h-5 mr-2" />
                    Request Private Session
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};
